/*
 * Copyright (c) GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, Router} from '@angular/router';
import {FabConfirmationDialogComponent} from '@fabric/components/confirmation-dialog';
import {FabMessageService} from '@fabric/components/message';
import {
  ColDef,
  GridApi,
  GridReadyEvent,
  RowSelectionOptions,
  SizeColumnsToContentStrategy,
  SizeColumnsToFitGridStrategy,
  SizeColumnsToFitProvidedWidthStrategy
} from 'ag-grid-community';
import {DISMISS_MSG, FCORG_BLACKLIST} from '../../../../common/constants';
import {AutocompleteFilterComponent} from '../../../../core/filters/autocomplete-filter/autocomplete-filter.component';
import {StateService} from '../../../../core/state/state.service';
import {WhiteSpaceInputValidator} from '../../../../core/validators/white-space-input-validator';
import {GlobalScenario} from '../../../../models/global-scenario-dto.model';
import {GlobalScenarioRequest} from '../../../../models/global-scenario-request-dto.model';
import {VehicleScenario} from '../../../../models/vehicle-scenario-dto.model';
import {GlobalScenarioService} from '../../../../services/global-scenario-service';
import {VehicleScenarioService} from '../../../../services/vehicle-scenario.service';

@Component({
    selector: 'app-edit-add-global-scenario',
    templateUrl: './edit-add-global-scenario.component.html',
    styleUrls: ['./edit-add-global-scenario.component.scss'],
    standalone: false
})
export class EditAddGlobalScenarioComponent implements OnInit {
  memberGridApi: GridApi;
  availableGridApi: GridApi;
  showSpinner: boolean;
  noData: boolean;
  isEdit: boolean;
  isNameChange: boolean;
  isHidden = false;

  pageSize: number;

  searchVehicleScenarioId: number;

  vehicleScenarios: VehicleScenario[] = [];
  displayedVehicleScenarios: VehicleScenario[] = [];
  selectedVehicleScenario: any;
  memberVehicleScenarios: VehicleScenario[] = [];
  selectedMemberScenario: any;

  selectedGlobalScenario: GlobalScenario = new GlobalScenario();
  globalScenarioRequest: GlobalScenarioRequest = new GlobalScenarioRequest();


  displayedColumns: string[] = ['fcorg', 'vehScenId', 'vehScenName'];
  columnDefs: ColDef[] = [
    {headerName: 'FCORG', field: 'fcorg', sortable: true, filter: AutocompleteFilterComponent},
    {headerName: 'Scenario ID', field: 'vehScenId', sortable: true, filter: AutocompleteFilterComponent},
    {headerName: 'Scenario Name', field: 'vehScenName', sortable: true, filter: AutocompleteFilterComponent},
  ];
  autoSizeStrategy:
    | SizeColumnsToFitGridStrategy
    | SizeColumnsToFitProvidedWidthStrategy
    | SizeColumnsToContentStrategy = {
    type: "fitGridWidth",
  };
  rowSelection: RowSelectionOptions = {
    mode: 'singleRow',
    checkboxes: false,
    enableClickSelection: true,
    enableSelectionWithoutKeys: true,
  };

  originalGlobalScenarioName: string;
  displayHeader: string;
  searchVehicleScenarioIdsString: string;

  globalScenarioForm: FormGroup;

  constructor(public snackBar: FabMessageService,
              private readonly globalScenarioService: GlobalScenarioService,
              private readonly vehicleScenarioService: VehicleScenarioService,
              private readonly stateService: StateService,
              private readonly dialog: MatDialog,
              private route: ActivatedRoute,
              readonly router: Router) {
  }

  ngOnInit(): void {
    this.selectedVehicleScenario = null;
    this.displayHeader = 'Create Global Scenarios';
    this.route.queryParams
    .subscribe(params => {
      this.isEdit = (params.isEdit === 'true');
      if (this.isEdit) {
        this.displayHeader = 'Edit Global Scenarios';
        this.selectedGlobalScenario.globalScenarioName = params.globalScenarioName;
        this.originalGlobalScenarioName = params.globalScenarioName;
        this.selectedGlobalScenario.id = params.globalScenarioId;
        this.loadMemberVehicleScenarios(this.selectedGlobalScenario.id);
      }
      }
    );
    this.globalScenarioForm = new FormGroup({
      globalScenarioName: new FormControl(this.selectedGlobalScenario.globalScenarioName,
        [Validators.required, WhiteSpaceInputValidator.noWhitespaceValidator]),
    });

  }

  onAvailableGridReady(params: GridReadyEvent) {
    this.availableGridApi = params.api;
    this.getVehicleScenarios();

  }

  onMemberGridReady(params: GridReadyEvent) {
    this.memberGridApi = params.api;
  }

  /**
   * Navigates back to global scenario page
   */
  openGlobalScenarioPage(): void {
    this.router.navigate(['/admin/manage-global-scenarios']);
  }

  /**
   * Fetches the vehicle scenarios
   */
  getVehicleScenarios(): void {
    this.showSpinner = true;
    this.vehicleScenarioService.getVehicleScenarios(this.stateService.getUserGmid()).subscribe({
      next: data => {
        this.vehicleScenarios = data.filter(element => {
          return element.fcorg !== "GMTVP" && !FCORG_BLACKLIST.includes(element.fcorg);
        });
      },
      error: err => {
        this.errorHandle(err);
      },
      complete: () => {
        this.displayedVehicleScenarios = this.filterVehicleScenarios(this.vehicleScenarios);
        this.showSpinner = false;
        this.noData = (this.vehicleScenarios.length === 0);
      }
    })
  }

  /**
   * Fetch vehicle scenarios attached to global scenario
   * @param globalScenarioId global scenario id
   */
  loadMemberVehicleScenarios(globalScenarioId: number): void {
    this.globalScenarioService.getMemberVehicleScenarios(globalScenarioId).subscribe(
      data => {
        this.memberVehicleScenarios = data;
      },
      err => {
        this.errorHandle(err);
      },
      () => {
        this.setAvailableVehicleScenarioData();
        this.noData = (this.memberVehicleScenarios.length === 0);
      }
    );
  }

  /**
   * Event for selecting a scenario
   * @param event the event object
   */
  onSelect(event) {
    if (event.node.selected) {
      this.selectedVehicleScenario = (event.data);
    } else if (event.data === this.selectedVehicleScenario) {
      this.selectedVehicleScenario = null;
    }
  }

  /**
   * Event for selecting a member scenario
   * @param event the event object
   */
  onSelectMember(event) {
    if (event.node.selected) {
      this.selectedMemberScenario = (event.data);
    } else if (event.data === this.selectedMemberScenario) {
      this.selectedMemberScenario = null;
    }
  }

  /**
   * Send Post request to create a new global scenario
   */
  onCreate(): void {
    this.showSpinner = true;
    if (this.memberVehicleScenarios.length > 0) {
      this.globalScenarioService.createGlobalScenario(this.selectedGlobalScenario.globalScenarioName,
        this.stateService.getUserGmid(), this.memberVehicleScenarios).subscribe({
        error: (error) => {
          this.errorHandle(error);
          this.getVehicleScenarios();
        },
        complete: () => {
          this.getVehicleScenarios();
          this.showSpinner = false;
          this.snackBar.displaySuccess('Global Scenario Created!', DISMISS_MSG);
          this.openGlobalScenarioPage();
        }
      });
    } else {
      this.showSpinner = false;
      this.snackBar.displayError('No Member Scenarios have been Selected. ' +
        'The Scenario will NOT be created!', DISMISS_MSG);
    }
  }

  /**
   * Send Post Request to update an existing global scenario
   */
  onUpdate(): void {
    this.showSpinner = true;
    if (this.memberVehicleScenarios.length > 0) {
      this.isNameChange = (this.originalGlobalScenarioName !== this.selectedGlobalScenario.globalScenarioName);
      this.globalScenarioRequest.globalScenario = this.selectedGlobalScenario;
      this.globalScenarioRequest.globalScenario.gmid = this.stateService.getUserGmid();
      this.globalScenarioRequest.vehicleScenarios = this.memberVehicleScenarios;
      this.globalScenarioService.updateGlobalScenario(this.globalScenarioRequest, this.isNameChange)
      .subscribe({
        error: (error) => {
          this.errorHandle(error);
        },
        complete: () => {
          this.snackBar.displaySuccess('Global Scenario UPDATED!', DISMISS_MSG);
          this.showSpinner = false;
          this.openGlobalScenarioPage();
        }
      });
    } else {
      this.showSpinner = false;
      this.snackBar.displayError('No Member Scenarios have been Selected. ' +
        'The Scenario will NOT be updated!', DISMISS_MSG);
    }
  }

  /**
   * Add selected vehicle scenario to the global scenario
   * @param vehicleScenario vehicle scenario
   */
  addToGlobalScenario(vehicleScenario: VehicleScenario): void {
    if (this.addToGlobalScenarioValidator(vehicleScenario)) {
      this.memberVehicleScenarios = this.memberVehicleScenarios.concat(vehicleScenario);
      this.setAvailableVehicleScenarioData();
    }
  }

  /**
   * Validator for adding a vehicle scenario to the global scenario
   * @param selectedVehicleScenario selected vehicle scenario
   */
  addToGlobalScenarioValidator(selectedVehicleScenario: VehicleScenario): boolean {
    for (const vehicleScenario of this.memberVehicleScenarios) {
      if (selectedVehicleScenario.fcorg === vehicleScenario.fcorg) {
        this.openFcorgAlreadyIsUseConfirmation(selectedVehicleScenario);
        return false;
      }
    }
    return true;
  }

  /**
   * Remove selected vehicle scenario from the global scenario
   * @param vehicleScenario vehicle scenario
   */
  removeFromGlobalScenario(vehicleScenario: any): void {
    this.memberVehicleScenarios = this.memberVehicleScenarios
    .filter(obj => obj.vehScenId !== vehicleScenario.vehScenId);
    this.setAvailableVehicleScenarioData();
  }

  /**
   * Search for vehicle scenario by id, and add the vehicle scenario to the global scenario
   * @param vehicleScenarioId vehicle scenario id
   */
  onSearchVehicleScenario(vehicleScenarioId: number): void {
    const localVehicleScenario = this.vehicleScenarios.find(obj =>
      obj.vehScenId == vehicleScenarioId);
    if (localVehicleScenario != null) {
      this.addToGlobalScenario(localVehicleScenario);
    } else {
      this.openInvalidIdConfirmation(vehicleScenarioId);
    }
    this.searchVehicleScenarioId = null;
  }

  /**
   * Split the vehicleScenarioIdsString by comma and format ids into an array
   * @param vehicleScenarioIdsString all vehicle scenario ids passed in by user formatted in a string
   */
  onSearchVehicleScenarios(vehicleScenarioIdsString: string): void {
    let vehicleScenarioIdsArray: number[] = [];
    vehicleScenarioIdsArray = vehicleScenarioIdsString.split(',').map(id => Number(id));
    for (const currentId of vehicleScenarioIdsArray) {
      this.onSearchVehicleScenario(currentId);
    }
  }

  /**
   * Open confirmation dialog for if a vehicle scenario of the same fcorg already exists in the member scenario
   * @param vehicleScenario selected vehicle scenario
   */
  openFcorgAlreadyIsUseConfirmation(vehicleScenario: VehicleScenario): void {
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: 'The Member Scenarios already contain a Scenario representing FCORG: ' + vehicleScenario.fcorg + '.'
          + ' The Scenario ID: ' + vehicleScenario.vehScenId + ' has NOT been added to the Member Scenarios list.',
        yesLabel: 'Ok',
        actionColor: 'primary'
      },
      disableClose: true
    });
  }

  /**
   * Open confirmation dialog for if a vehicle scenario id is invalid
   * @param vehicleScenarioId vehicle scenario id
   */
  openInvalidIdConfirmation(vehicleScenarioId: number): void {
    this.dialog.open(FabConfirmationDialogComponent, {
      width: '400px',
      data: {
        title: 'Confirmation',
        message: 'The Scenario ID: ' + vehicleScenarioId + ' is invalid and has NOT been added to the Member Scenarios list.',
        yesLabel: 'Ok',
        actionColor: 'primary'
      },
      disableClose: true
    });
  }

  /**
   * Set the paginated data for the available vehicle scenario table
   */
  setAvailableVehicleScenarioData(): void {
    this.selectedVehicleScenario = null;
    this.displayedVehicleScenarios = this.filterVehicleScenarios(this.vehicleScenarios);
  }

  /**
   * Filters the vehicle scenarios to match the fcorg filter and show hidden
   * @param vehicleScenarios the list of scenarios to filter
   */
  filterVehicleScenarios(vehicleScenarios: VehicleScenario[]): VehicleScenario[] {
    return vehicleScenarios.filter(element => {
      return (element.isHidden === this.isHidden || this.isHidden)
        && !this.memberVehicleScenarios.map(scenario => scenario.vehScenId).includes(element.vehScenId);
    });
  }

  /**
   * display message on error.
   * @param error custom error message.
   */
  errorHandle(error: string): void {
    this.showSpinner = false;
    this.selectedVehicleScenario = null;
    this.snackBar.displayError(error, DISMISS_MSG);
  }
}
